import { marked } from 'marked'
import { normalizeBundle } from './schema-io.mjs'

export const defaultNodes = [
  {
    id: 'claim',
    name: '论点',
    english: 'Claim',
    definition: '作者提出并需要论据支撑的主张或结论。',
    color: '#f6a6a6',
    prefix: 'C',
  },
  {
    id: 'evidence',
    name: '证据',
    english: 'Evidence',
    definition: '用于支持或反驳主张的史料、引文、事实或实例。',
    color: '#a9cfff',
    prefix: 'E',
  },
  {
    id: 'context',
    name: '背景',
    english: 'Context',
    definition: '理解论证所需的历史情境、时代背景或概念界定。',
    color: '#bde7c2',
    prefix: 'B',
  },
  {
    id: 'analysis',
    name: '分析',
    english: 'Analysis',
    definition: '对史料、因果机制或不同解释的分析与推理。',
    color: '#d9c7ff',
    prefix: 'A',
  },
  {
    id: 'focus',
    name: '焦点',
    english: 'Focus',
    definition: '本文集中探讨的研究问题、对象与范围。',
    color: '#f3d38b',
    prefix: 'F',
  },
].map((item, index) => ({ ...item, legacyId: item.id, id: index + 1 }))
export const defaultLinks = [
  {
    id: 'support',
    name: '支撑',
    english: 'Support',
    definition: '源节点为目标节点的主张提供依据。',
    color: '#2d67b1',
    dash: 'solid',
  },
  {
    id: 'elaborate',
    name: '阐释',
    english: 'Elaborate',
    definition: '源节点展开、解释或细化目标节点。',
    color: '#d24b4b',
    dash: 'solid',
  },
  {
    id: 'contextualize',
    name: '背景化',
    english: 'Contextualize',
    definition: '源节点为目标节点提供历史或概念背景。',
    color: '#7d6cc8',
    dash: 'dashed',
  },
  {
    id: 'focus_on',
    name: '聚焦',
    english: 'Focus on',
    definition: '源节点将讨论引向目标节点的研究问题。',
    color: '#8e99a8',
    dash: 'dotted',
  },
].map((item, index) => ({ ...item, legacyId: item.id, id: index + 1 }))
export const defaultFacets = [
  {
    id: 'person',
    name: '人物',
    english: 'Person',
    definition: '论证单元涉及的历史人物与研究者。',
    terms: '陈寅恪,李克用,李嗣源,石敬瑭,欧阳修,安守忠,李红岩,孔子,朱熹,王阳明,龚自珍,韩愈,葛兆光,邓小南',
  },
  {
    id: 'location',
    name: '地点',
    english: 'Location',
    definition: '论证单元涉及的地理区域与地点。',
    terms: '中原,河北,河东,河朔,华北,开封,代北,洛阳,北京,西方,中国,雁门,山西',
  },
  {
    id: 'keyword',
    name: '关键词',
    english: 'Keyword',
    definition: '论证中的核心概念、时期与群体。',
    terms: '五代,宋初,胡/汉,沙陀,粟特,汉化,民族,文化,六经皆史,真理,历史意识,经史,永恒,流变',
  },
]
const plainInline = (tokens) =>
  (tokens || [])
    .map((t) =>
      t.type === 'link' && /^\[\d+\]/.test(t.raw)
        ? t.raw
        : t.type === 'image'
          ? t.text
          : t.tokens
            ? plainInline(t.tokens)
            : t.text || t.raw || '',
    )
    .join('')
const pad = (n) => String(n).padStart(3, '0')

export function splitSentences(text) {
  const citations = [...text.matchAll(/\[\d+\](?:\([^\n)]*\))?/g)]
  const boundaries = [...new Intl.Segmenter('zh', { granularity: 'sentence' }).segment(text)].map(
    (s) => s.index,
  )
  // Keep post-sentence references attached to the sentence they document.
  const starts = [
    ...new Set(
      boundaries.map((index) => {
        const citation = citations.find((c) => index > 0 && index >= c.index && index < c.index + c[0].length)
        return citation ? citation.index + citation[0].length : index
      }),
    ),
  ]
    .sort((a, b) => a - b)
    .filter((index) => index < text.length)
  return starts.map((start, i) => ({
    text: text.slice(start, starts[i + 1]),
    start,
    end: starts[i + 1] ?? text.length,
  }))
}

export function inferType(text) {
  if (/本文|本研究|希望讨论|研究的问题|所欲达到/.test(text)) return 'focus'
  if (/指出|记载|墓志|《|先生|史料|例如|为例/.test(text)) return 'evidence'
  if (/背景|时期|时代|所谓|意味着|世纪/.test(text)) return 'context'
  if (/认为|应该说|质言之|总之|主张|重要|表明|反映|因此/.test(text)) return 'claim'
  return 'analysis'
}

export function parseDocument(source, id = 'document', { macroOnly = true } = {}) {
  // Normalize only heading syntax. The original Markdown remains in source.
  const normalized = source.replace(/^([\t \u3000]*)([一二三四五六七八九十百]+[、．][^\n]{1,70})$/gm, '## $2')
  const tokens = marked.lexer(normalized)
  const document = {
    version: 1,
    id,
    source,
    title: '',
    chapters: [],
    blocks: [],
    paragraphs: [],
    units: [],
    edges: [],
    annotationSource: 'heuristic',
  }
  const headingDepths = tokens.filter((t) => t.type === 'heading' && t.depth > 1).map((t) => t.depth)
  const chapterDepth = Math.min(...headingDepths)
  let chapter = null,
    sentenceNumber = 0
  for (const token of tokens) {
    if (token.type === 'space') continue
    let text = token.tokens ? plainInline(token.tokens) : token.text || token.raw.trim()
    let titleMetadata = false
    if (token.type === 'heading') {
      if (!document.title && token.depth === 1) document.title = text
      else if (token.depth === chapterDepth || token.depth === 1) {
        chapter = {
          id: `CH${pad(document.chapters.length + 1)}`,
          title: text,
          number: document.chapters.length + 1,
        }
        document.chapters.push(chapter)
      }
      document.blocks.push({ kind: 'heading', text, depth: token.depth, chapterId: chapter?.id })
      continue
    }
    if (!document.title) {
      document.title = text
        .split('\n')[0]
        .replace(/^["“]|["”]$/g, '')
        .trim()
      document.blocks.push({ kind: 'heading', text: document.title, depth: 1 })
      const rest = text.split('\n').slice(1).join('\n').trim()
      if (!rest) continue
      text = rest
      titleMetadata = true
    }
    const metadata =
      titleMetadata ||
      (!chapter &&
        (text.length < 65 ||
          /^(摘要|关键词|中图分类号|文献标识码|文章编号|作者|原发信息|复印期刊)/.test(text)))
    const paragraph = {
      id: `P${pad(document.paragraphs.length + 1)}`,
      number: document.paragraphs.length + 1,
      chapterId: chapter?.id || null,
      kind: metadata ? 'metadata' : 'paragraph',
      text,
      type: inferType(text),
      note: '',
      provenance: 'heuristic',
      sentences: splitSentences(text).map((s) => ({
        ...s,
        id: `S${pad(++sentenceNumber)}`,
        number: sentenceNumber,
      })),
    }
    document.paragraphs.push(paragraph)
    document.blocks.push({ kind: paragraph.kind, paragraphId: paragraph.id, text })
    if (metadata || macroOnly) continue
    let unit = null
    for (const sentence of paragraph.sentences) {
      const type = inferType(sentence.text)
      if (!unit || unit.type !== type || unit.sentenceIds.length >= 2) {
        unit = {
          id: `U${pad(document.units.length + 1)}`,
          paragraphId: paragraph.id,
          type,
          sentenceIds: [],
          note: '',
          provenance: 'heuristic',
        }
        document.units.push(unit)
      }
      unit.sentenceIds.push(sentence.id)
    }
  }
  if (!document.chapters.length) {
    const body = { id: 'CH001', title: '正文', number: 1 }
    document.chapters.push(body)
    document.paragraphs
      .filter((p) => p.kind === 'paragraph')
      .forEach((p) => {
        p.chapterId = body.id
      })
  }
  if (macroOnly) {
    document.units = []
    document.annotationSource = 'pending'
    document.macroStatus = 'pending'
    document.paragraphs.forEach((p) => {
      p.unitsStatus = 'pending'
      p.provenance = 'pending'
    })
    return normalizeBundle({ document, schemas: defaultNodes, links: defaultLinks }).document
  }
  let edgeNumber = 0
  const addEdge = (source, target, type, level, joint = '') =>
    document.edges.push({
      id: `R${pad(++edgeNumber)}`,
      source,
      target,
      type,
      level,
      joint,
      provenance: 'heuristic',
      note: '',
    })
  for (const chapterId of [null, ...document.chapters.map((c) => c.id)]) {
    const paragraphs = document.paragraphs.filter((p) => p.chapterId === chapterId && p.kind === 'paragraph')
    paragraphs
      .slice(1)
      .forEach((p, i) =>
        addEdge(
          p.id,
          paragraphs[i].id,
          p.type === 'evidence' ? 'support' : p.type === 'context' ? 'contextualize' : 'elaborate',
          'paragraph',
        ),
      )
  }
  for (const p of document.paragraphs) {
    const units = document.units.filter((u) => u.paragraphId === p.id)
    if (units.length < 2) continue
    const target = units.find((u) => u.type === 'claim') || units[0]
    const evidence = units.filter((u) => u.type === 'evidence' && u.id !== target.id)
    units
      .filter((u) => u.id !== target.id)
      .forEach((u) =>
        addEdge(
          u.id,
          target.id,
          u.type === 'context'
            ? 'contextualize'
            : u.type === 'focus'
              ? 'focus_on'
              : u.type === 'evidence'
                ? 'support'
                : 'elaborate',
          'unit',
          evidence.length > 1 && u.type === 'evidence' ? `${p.id}-premises` : '',
        ),
      )
  }
  return normalizeBundle({ document, schemas: defaultNodes, links: defaultLinks }).document
}

export function unitText(unit, document) {
  return (
    document.paragraphs
      .find((p) => p.id === unit.paragraphId)
      ?.sentences.filter((s) => unit.sentenceIds.includes(s.id))
      .map((s) => s.text)
      .join('') || ''
  )
}

export function extractFacets(document, facets) {
  return facets.map((facet) => {
    const found = new Map()
    const terms = [
      ...new Set(
        (facet.terms || '')
          .split(/[,，\n;；]/)
          .map((t) => t.trim())
          .filter(Boolean),
      ),
    ]
    for (const unit of document.units) {
      const text = document.engineVersion === 2 ? '' : unitText(unit, document)
      const values =
        unit.facets?.[facet.id] ??
        (document.engineVersion === 2
          ? []
          : terms
              .filter((t) => text.includes(t))
              .map((value) => ({ value, count: text.split(value).length - 1 })))
      for (const value of values) {
        const term = value.value
        if (!term) continue
        const entity = found.get(term) || { term, unitIds: [], count: 0 }
        if (!entity.unitIds.includes(unit.id)) entity.unitIds.push(unit.id)
        entity.count += value.count || 1
        found.set(term, entity)
      }
    }
    const entities = [...found.values()].sort((a, b) => b.count - a.count)
    return { ...facet, entities }
  })
}

export function validateDocument(document, nodes, links) {
  if (
    !document ||
    document.version !== 1 ||
    !Array.isArray(document.paragraphs) ||
    !Array.isArray(document.units) ||
    !Array.isArray(document.edges) ||
    !Array.isArray(document.chapters) ||
    !Array.isArray(document.blocks) ||
    typeof document.source !== 'string'
  )
    throw new Error('不是有效的 ArguLoom 文档。')
  const nodeTypes = new Set(nodes.map((n) => n.id)),
    linkTypes = new Set(links.map((l) => l.id))
  const allIds = [...document.chapters, ...document.paragraphs, ...document.units, ...document.edges].map(
    (n) => n.id,
  )
  if (
    allIds.some((id) => typeof id !== 'string' || !/^[\w-]+$/.test(id)) ||
    new Set(allIds).size !== allIds.length
  )
    throw new Error('编号必须有效且唯一。')
  const chapters = new Set(document.chapters.map((c) => c.id)),
    sentences = new Set()
  for (const p of document.paragraphs) {
    if (!nodeTypes.has(p.type) || !Array.isArray(p.sentences) || (p.chapterId && !chapters.has(p.chapterId)))
      throw new Error(`段落 ${p.id} 的类型、章节或句子不合法。`)
    if (p.sentences.map((s) => s.text).join('') !== p.text)
      throw new Error(`${p.id} 的句子必须完整还原段落文本。`)
    for (const s of p.sentences) {
      if (typeof s.id !== 'string' || sentences.has(s.id)) throw new Error('句子编号重复或无效。')
      sentences.add(s.id)
    }
    const assigned = new Set()
    for (const u of document.units.filter((u) => u.paragraphId === p.id)) {
      if (!nodeTypes.has(u.type) || !Array.isArray(u.sentenceIds) || !u.sentenceIds.length)
        throw new Error(`${u.id} 的类型或句子范围不合法。`)
      const indices = u.sentenceIds.map((id) => p.sentences.findIndex((s) => s.id === id))
      if (indices.some((n, i) => n < 0 || (i && n !== indices[i - 1] + 1)))
        throw new Error(`${u.id} 必须引用同一段内连续的句子。`)
      for (const id of u.sentenceIds) {
        if (assigned.has(id)) throw new Error(`${p.id} 的论证单元范围重叠。`)
        assigned.add(id)
      }
    }
    if (
      p.kind === 'paragraph' &&
      (assigned.size || p.unitsStatus !== 'pending') &&
      assigned.size !== p.sentences.length
    )
      throw new Error(`${p.id} 存在未归属论证单元的句子。`)
  }
  const paragraphs = new Set(document.paragraphs.map((p) => p.id))
  if (document.units.some((u) => !paragraphs.has(u.paragraphId)))
    throw new Error('论证单元引用了不存在的段落。')
  const ids = new Set([...paragraphs, ...document.units.map((u) => u.id)])
  for (const edge of document.edges) {
    if (
      !ids.has(edge.source) ||
      !ids.has(edge.target) ||
      edge.source === edge.target ||
      !linkTypes.has(edge.type)
    )
      throw new Error(`关系 ${edge.id} 的端点或类型无效。`)
    if (
      !['paragraph', 'unit'].includes(edge.level) ||
      [edge.source, edge.target].some((id) => paragraphs.has(id) !== (edge.level === 'paragraph'))
    )
      throw new Error(`关系 ${edge.id} 不能跨越段落和单元层级。`)
  }
  if (document.engineVersion === 2) {
    const pairs = new Set()
    for (const edge of document.edges) {
      const key = JSON.stringify([edge.level, [edge.source, edge.target].sort()])
      if (pairs.has(key)) throw new Error('同一对节点只能有一条单向关系。')
      pairs.add(key)
    }
  }
  if (document.blocks.some((b) => b.paragraphId && !paragraphs.has(b.paragraphId)))
    throw new Error('文本块引用了不存在的段落。')
  return document
}
